import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useBlocker, useNavigate } from "@tanstack/react-router";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { pickAdminFormPaths } from "@/features/form-editor/admin-form-editor-i18n-paths";
import type { FormEditorEntity } from "@/features/form-editor/form-editor-entity.types";
import { useFormEditorDirty } from "@/features/form-editor/hooks/use-form-editor-dirty";
import { useFormEditorPreviewSchema } from "@/features/form-editor/hooks/use-form-editor-preview-schema";
import {
	buildFormDefinitionScopeFields,
	mergeFieldUiIntoLayout,
	toApiFieldDefinition,
} from "@/features/form-editor/lib/api-form-definition-payload.util";
import {
	buildFormEditorPaths,
	type FormEditorScope,
	type FormEditorVariant,
	navigateToFullFormPreview,
} from "@/features/form-editor/lib/build-form-editor-paths";
import {
	isSiblingEntityTypeFormSurface,
	resolveFormEditorLeave,
	shouldPromptForFormEditorLeave,
} from "@/features/form-editor/lib/form-editor-leave";
import { builderStateToDynamicFormSchema } from "@/features/form-editor/lib/builder-to-dynamic-schema";
import {
	prepareFormDefinitionEditorFormRules,
	prepareFormDefinitionEditorSchema,
} from "@/features/form-editor/lib/prepare-ticket-type-editor-schema";
import {
	cancelFormEditorRelease,
	claimFormEditorType,
	scheduleFormEditorRelease,
} from "@/features/form-editor/state/entity-type-form-mode-store";
import { useFormEditorStore } from "@/features/form-editor/state/form-editor-store";
import { useAppTranslation } from "@/hooks/use-app-translation";
import type { FormDefinitionScopeQuery } from "@/services/form-definition";
import {
	formDefinitionKeys,
	formDefinitionQueryOptions,
} from "@/services/form-definition";
import {
	useActivateFormDefinition,
	useCreateFormDefinition,
	useUpdateFormDefinition,
} from "@/services/form-definition/form-definition.mutations";
import { serializeLayoutJsonWireForPersistence } from "@/services/form-definition/layout-json-envelope.schema";
import { showApiErrorToast, showSuccessToast } from "@/utils/toast.utils";

export type FormEditorSaveStrategy = "simple" | "versionedActivate";

export interface UseFormDefinitionEditorPersistenceInput {
	entity: FormEditorEntity;
	typeId: string;
	scope: FormEditorScope;
	orgId?: string;
	variant: FormEditorVariant;
	canEdit?: boolean;
	mutationOrganizationId?: string;
	saveStrategy?: FormEditorSaveStrategy;
	/** When set, cancel reverts then runs this instead of default navigation. */
	onCancelNavigate?: () => void;
	/** When set, successful save runs this instead of default navigation. */
	onSaveNavigate?: () => void;
	/**
	 * Drawer Form and page Form are one editor. Moving between them keeps the draft.
	 */
	retainSiblingFormSurfaces?: boolean;
}

function buildFormId(entity: FormEditorEntity, typeId: string): string {
	switch (entity) {
		case "ticket":
			return `ticket-type-form-${typeId}`;
		case "job":
			return `job-type-form-${typeId}`;
		case "asset":
			return `asset-type-form-${typeId}`;
		case "record":
			return `record-type-form-${typeId}`;
	}
}

function buildScopeQuery(
	entity: FormEditorEntity,
	typeId: string,
): FormDefinitionScopeQuery {
	switch (entity) {
		case "ticket":
			return { TicketTypeId: typeId };
		case "job":
			return { JobTypeId: typeId };
		case "asset":
			return { AssetTypeId: typeId };
		case "record":
			return { RecordTypeId: typeId };
	}
}

function buildScopeFields(entity: FormEditorEntity, typeId: string) {
	switch (entity) {
		case "ticket":
			return buildFormDefinitionScopeFields({
				ticketTypeId: typeId,
				jobTypeId: null,
				assetTypeId: null,
			});
		case "job":
			return buildFormDefinitionScopeFields({
				ticketTypeId: null,
				jobTypeId: typeId,
				assetTypeId: null,
			});
		case "asset":
			return buildFormDefinitionScopeFields({
				ticketTypeId: null,
				jobTypeId: null,
				assetTypeId: typeId,
			});
		case "record":
			return buildFormDefinitionScopeFields({
				ticketTypeId: null,
				jobTypeId: null,
				assetTypeId: null,
				recordTypeId: typeId,
			});
	}
}

/**
 * Loads, hydrates, and persists form-definition editor state for standalone and full routes.
 */
export function useFormDefinitionEditorPersistence({
	entity,
	typeId,
	scope,
	orgId,
	variant,
	canEdit = true,
	mutationOrganizationId,
	saveStrategy: saveStrategyProp,
	onCancelNavigate,
	onSaveNavigate,
	retainSiblingFormSurfaces = false,
}: UseFormDefinitionEditorPersistenceInput) {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const paths = useMemo(() => pickAdminFormPaths(entity), [entity]);
	const revertToSavedSchema = useFormEditorStore((s) => s.revertToSavedSchema);
	const setFormEditorEntity = useFormEditorStore((s) => s.setFormEditorEntity);

	const saveStrategy =
		saveStrategyProp ?? (variant === "full" ? "versionedActivate" : "simple");

	const routePaths = useMemo(
		() =>
			buildFormEditorPaths({
				scope,
				orgId,
				entity,
				typeId,
				variant,
			}),
		[entity, orgId, scope, typeId, variant],
	);

	const formId = buildFormId(entity, typeId);
	const formDefinitionQueryParams = useMemo(
		() => buildScopeQuery(entity, typeId),
		[entity, typeId],
	);

	const formDefinitionRequestOptions = useMemo(
		() =>
			mutationOrganizationId && mutationOrganizationId.length > 0
				? { organizationId: mutationOrganizationId }
				: undefined,
		[mutationOrganizationId],
	);

	const previewSchema = useFormEditorPreviewSchema(formId, false);
	const remoteFormQuery = useQuery(
		formDefinitionQueryOptions.current(
			formDefinitionQueryParams,
			formDefinitionRequestOptions,
		),
	);

	const createFormDefinition = useCreateFormDefinition();
	const updateFormDefinition = useUpdateFormDefinition();
	const activateFormDefinition = useActivateFormDefinition();

	const [previewOpen, setPreviewOpen] = useState(false);

	useLayoutEffect(() => {
		setFormEditorEntity(entity);
	}, [entity, setFormEditorEntity]);

	const typeKey = `${entity}:${typeId}`;
	if (claimFormEditorType(typeKey)) {
		useFormEditorStore.getState().reset();
	}

	useLayoutEffect(() => {
		cancelFormEditorRelease(typeKey);
		return () => {
			scheduleFormEditorRelease(typeKey);
		};
	}, [typeKey]);

	const formDefinition = remoteFormQuery.data;

	const editorHydrationKey = remoteFormQuery.isSuccess
		? formDefinition != null
			? `${typeId}:${formDefinition.id}:${formDefinition.version}`
			: `${typeId}:new`
		: null;

	const { isDirty, markBaselineCurrent } =
		useFormEditorDirty(editorHydrationKey);
	const isDirtyRef = useRef(isDirty);
	isDirtyRef.current = isDirty;
	const saveInFlightRef = useRef(false);
	const [isSaveInFlight, setIsSaveInFlight] = useState(false);
	const leaveBlockerRef = useRef<{
		status: "idle" | "blocked";
		reset: () => void;
	}>({
		status: "idle",
		reset: () => undefined,
	});
	const [isApplyingLeave, setIsApplyingLeave] = useState(false);

	const editorSchema = useMemo(() => {
		if (!remoteFormQuery.isSuccess || formDefinition == null) return undefined;
		return prepareFormDefinitionEditorSchema(formId, formDefinition);
	}, [remoteFormQuery.isSuccess, formDefinition, formId]);

	const editorFormRules = useMemo(() => {
		if (!remoteFormQuery.isSuccess || formDefinition == null) return [];
		return prepareFormDefinitionEditorFormRules(formDefinition);
	}, [remoteFormQuery.isSuccess, formDefinition]);

	const defaultCancelNavigate = useCallback(() => {
		if (variant === "full") {
			navigateToFullFormPreview(navigate, { scope, orgId, entity, typeId });
			return;
		}
		void navigate({ to: routePaths.listHref as never });
	}, [entity, navigate, orgId, routePaths.listHref, scope, typeId, variant]);

	const defaultSaveNavigate = useCallback(() => {
		defaultCancelNavigate();
	}, [defaultCancelNavigate]);

	const navigateAfterCancel = useCallback(() => {
		if (onCancelNavigate) {
			onCancelNavigate();
			return;
		}
		defaultCancelNavigate();
	}, [defaultCancelNavigate, onCancelNavigate]);

	const handleCancel = useCallback(() => {
		if (isDirtyRef.current) {
			navigateAfterCancel();
			return;
		}
		revertToSavedSchema(editorSchema, editorFormRules);
		markBaselineCurrent();
		navigateAfterCancel();
	}, [
		editorFormRules,
		editorSchema,
		markBaselineCurrent,
		navigateAfterCancel,
		revertToSavedSchema,
	]);

	const handleSave = useCallback(async (options?: { navigateAfterSave?: boolean }) => {
		if (!canEdit || saveInFlightRef.current) return false;
		saveInFlightRef.current = true;
		setIsSaveInFlight(true);
		const navigateAfterSave = options?.navigateAfterSave !== false;
		const { formTitle, formDescription, rootIds, nodes, formRules } =
			useFormEditorStore.getState();
		const schema = builderStateToDynamicFormSchema(
			{ formTitle, formDescription, rootIds, nodes },
			formId,
			{ formEditorEntity: entity },
		);
		const layoutForApi = mergeFieldUiIntoLayout(
			schema.layoutJson,
			schema.fieldDefinitions,
		);
		const layoutJson = serializeLayoutJsonWireForPersistence(
			{
				title: schema.title ?? "",
				description: schema.description ?? "",
				layout: layoutForApi,
			},
			formRules,
		);
		const apiFieldDefinitions =
			schema.fieldDefinitions.map(toApiFieldDefinition);
		const saveBody = {
			layoutJson,
			fieldDefinitions: apiFieldDefinitions,
		};

		try {
			const current = remoteFormQuery.data;
			if (saveStrategy === "versionedActivate") {
				const canUpdateInPlace =
					Boolean(current?.id) && current?.active === false;
				let formDefinitionId: string | null = null;
				if (canUpdateInPlace && current?.id) {
					await updateFormDefinition.mutateAsync({
						id: current.id,
						body: saveBody,
						requestOptions: formDefinitionRequestOptions,
					});
					formDefinitionId = current.id;
				} else {
					const result = await createFormDefinition.mutateAsync({
						body: {
							...saveBody,
							...buildScopeFields(entity, typeId),
						},
						requestOptions: formDefinitionRequestOptions,
					});
					formDefinitionId = result.affectedId;
				}
				if (formDefinitionId) {
					await activateFormDefinition.mutateAsync({
						id: formDefinitionId,
						requestOptions: formDefinitionRequestOptions,
					});
				}
				await queryClient.invalidateQueries({
					queryKey: formDefinitionKeys.current(
						formDefinitionQueryParams,
						mutationOrganizationId,
					),
				});
			} else if (current?.id) {
				await updateFormDefinition.mutateAsync({
					id: current.id,
					body: saveBody,
					requestOptions: formDefinitionRequestOptions,
				});
			} else {
				await createFormDefinition.mutateAsync({
					body: {
						...saveBody,
						...buildScopeFields(entity, typeId),
					},
					requestOptions: formDefinitionRequestOptions,
				});
			}

			markBaselineCurrent();
			isDirtyRef.current = false;

			showSuccessToast({
				message: t(paths.formEditor.saveSuccess),
			});

			if (navigateAfterSave && leaveBlockerRef.current.status === "blocked") {
				leaveBlockerRef.current.reset();
			}
			if (navigateAfterSave) {
				if (onSaveNavigate) {
					onSaveNavigate();
				} else {
					defaultSaveNavigate();
				}
			}
			return true;
		} catch (error) {
			showApiErrorToast({
				message: t(paths.formEditor.saveError),
				error,
			});
			return false;
		} finally {
			saveInFlightRef.current = false;
			setIsSaveInFlight(false);
		}
	}, [
		activateFormDefinition,
		canEdit,
		createFormDefinition,
		defaultSaveNavigate,
		entity,
		formDefinitionQueryParams,
		formDefinitionRequestOptions,
		formId,
		markBaselineCurrent,
		mutationOrganizationId,
		onSaveNavigate,
		paths.formEditor.saveError,
		paths.formEditor.saveSuccess,
		queryClient,
		remoteFormQuery.data,
		saveStrategy,
		t,
		typeId,
		updateFormDefinition,
	]);

	const isSaving =
		createFormDefinition.isPending ||
		updateFormDefinition.isPending ||
		activateFormDefinition.isPending ||
		remoteFormQuery.isPending;

	const shouldBlockLeave = useCallback(
		({ next }: { next: { pathname: string; search: unknown } }) => {
			if (
				retainSiblingFormSurfaces &&
				isSiblingEntityTypeFormSurface(next, routePaths.detailHref)
			) {
				return false;
			}
			return shouldPromptForFormEditorLeave({
				isDirty: isDirtyRef.current,
				nextPathname: next.pathname,
				formBaseHref: routePaths.formBaseHref,
			});
		},
		[
			retainSiblingFormSurfaces,
			routePaths.detailHref,
			routePaths.formBaseHref,
		],
	);

	const leaveBlocker = useBlocker({
		shouldBlockFn: shouldBlockLeave,
		enableBeforeUnload: false,
		withResolver: true,
	});
	leaveBlockerRef.current =
		leaveBlocker.status === "blocked"
			? { status: "blocked", reset: leaveBlocker.reset }
			: { status: "idle", reset: () => undefined };

	const revertUnsavedEdits = useCallback(() => {
		revertToSavedSchema(editorSchema, editorFormRules);
		markBaselineCurrent();
		isDirtyRef.current = false;
	}, [editorFormRules, editorSchema, markBaselineCurrent, revertToSavedSchema]);

	const onStay = useCallback(() => {
		if (leaveBlocker.status !== "blocked" || isApplyingLeave) return;
		leaveBlocker.reset();
	}, [isApplyingLeave, leaveBlocker]);

	const onDiscard = useCallback(() => {
		if (
			leaveBlocker.status !== "blocked" ||
			isApplyingLeave ||
			saveInFlightRef.current
		) {
			return;
		}
		void resolveFormEditorLeave("discard", {
			revert: revertUnsavedEdits,
			save: async () => false,
			proceed: leaveBlocker.proceed,
			reset: leaveBlocker.reset,
		});
	}, [isApplyingLeave, leaveBlocker, revertUnsavedEdits]);

	const onApply = useCallback(() => {
		if (
			leaveBlocker.status !== "blocked" ||
			isApplyingLeave ||
			saveInFlightRef.current
		) {
			return;
		}
		setIsApplyingLeave(true);
		void resolveFormEditorLeave("apply", {
			revert: revertUnsavedEdits,
			save: () => handleSave({ navigateAfterSave: false }),
			proceed: () => {
				setIsApplyingLeave(false);
				leaveBlocker.proceed();
			},
			reset: () => {
				setIsApplyingLeave(false);
				leaveBlocker.reset();
			},
		});
	}, [handleSave, isApplyingLeave, leaveBlocker, revertUnsavedEdits]);

	return {
		paths,
		routePaths,
		formId,
		previewSchema,
		previewOpen,
		setPreviewOpen,
		remoteFormQuery,
		editorHydrationKey,
		editorSchema,
		editorFormRules,
		isDirty,
		canEdit,
		isSaving,
		handleCancel,
		handleSave,
		revertUnsavedEdits,
		unsavedPrompt: {
			isOpen: leaveBlocker.status === "blocked",
			isApplying: isApplyingLeave,
			isSaveInFlight,
			onStay,
			onDiscard,
			onApply,
		},
	};
}
